import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";

import { Sparkles, Send, X, Check, ChevronRight } from "lucide-react";
import MiniGridLoader from '@/components/MiniGridLoader';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useMetricDefinitions } from "@/lib/useMetricDefinitions";
import { hapticSuccess } from "@/utils/haptics";
import { useNavigate } from "react-router-dom";

interface ChatMessage {
  role: "assistant" | "user";
  content: string;
  updatedValues?: Record<string, any>;
}

interface FixInChatDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  selectedDate: Date;
  originalInput: string;
  suggestedValues: any;
  customMetrics: Array<{ metric_key: string; metric_name: string; metric_emoji: string; metric_unit: string; metric_type?: string }>;
  onLogValues: (values: Record<string, any>) => void;
  onEmojiExplosion?: (emojis: string[]) => void;
  userCredits?: number;
  isPremium?: boolean;
  freeFixAvailable?: boolean;
  onFreeFixUsed?: () => void;
}

export const FixInChatDialog = ({
  open,
  onOpenChange,
  userId,
  selectedDate,
  originalInput,
  suggestedValues,
  customMetrics,
  onLogValues,
  onEmojiExplosion,
  userCredits,
  isPremium,
  freeFixAvailable: freeFixAvailableProp = false,
  onFreeFixUsed,
}: FixInChatDialogProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasUsedFreeFix, setHasUsedFreeFix] = useState(false); // Track if free fix was used in this session
  const [freeFixFromDb, setFreeFixFromDb] = useState<boolean | null>(null); // Fetched from database
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea (grows upward since it's at the bottom of the dialog)
  const autoResizeInput = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, []);
  
  // Use database value if available, otherwise fall back to prop
  const freeFixAvailable = freeFixFromDb ?? freeFixAvailableProp;

  // Fetch metric definitions for display names (returns Record<string, MetricDefinition>)
  const { data: metricDefinitions } = useMetricDefinitions();

  // Build a set of valid metric keys (same validation as QuickEntryConfirmDialog)
  const validMetricKeys = useMemo(() => {
    const keys = new Set<string>([
      ...(metricDefinitions && typeof metricDefinitions === 'object' ? Object.keys(metricDefinitions) : []),
      ...customMetrics.map(cm => {
        // Extract slot number from metric_key (e.g., "custom_6" -> "6")
        const slotMatch = cm.metric_key.match(/^custom_(\d+)$/);
        if (slotMatch) {
          return `custom_${slotMatch[1]}_value`;
        }
        // If already in _value format, return as-is
        if (cm.metric_key.match(/^custom_(\d+)_value$/)) {
          return cm.metric_key;
        }
        return null; // Will be filtered out
      }).filter(Boolean) as string[]
    ]);
    return keys;
  }, [metricDefinitions, customMetrics]);

  // Helper to check if a metric key is valid
  const isValidMetricKey = (key: string) => {
    return validMetricKeys.has(key);
  };

  // Helper to get metric display info
  const getMetricInfo = (key: string) => {
    // Check standard metrics (metricDefinitions is a Record keyed by metric key)
    const standardMetric = metricDefinitions?.[key];
    if (standardMetric) {
      return { name: standardMetric.name, emoji: standardMetric.emoji, unit: standardMetric.unit };
    }
    // Check custom metrics - handle both "custom_X" and "custom_X_value" formats
    const customMetric = customMetrics.find(m => {
      if (m.metric_key === key) return true;
      // Also match custom_X_value to custom_X
      const valueMatch = key.match(/^custom_(\d+)_value$/);
      if (valueMatch) {
        return m.metric_key === `custom_${valueMatch[1]}`;
      }
      return false;
    });
    if (customMetric) {
      return { name: customMetric.metric_name, emoji: customMetric.metric_emoji, unit: customMetric.metric_unit };
    }
    return null; // Return null for invalid metrics instead of fallback
  };

  // Build comparison data for updated values (shows old vs new)
  const buildComparisonRows = (updatedValues: Record<string, any>) => {
    const rows: Array<{ key: string; name: string; emoji: string; unit: string; oldValue: number | null; newValue: number; changed: boolean }> = [];
    
    for (const [key, newValue] of Object.entries(updatedValues)) {
      if (newValue === undefined || newValue === null) continue;
      
      // Validate metric key exists in our definitions
      if (!isValidMetricKey(key)) {
        console.warn(`[FixInChatDialog] Filtered out invalid metric key in comparison: ${key}`);
        continue;
      }
      
      // Get old value from suggestedValues
      const oldValue = suggestedValues?.[key] ?? null;
      const changed = oldValue !== newValue;
      
      const info = getMetricInfo(key);
      if (!info) {
        console.warn(`[FixInChatDialog] Could not get metric info for: ${key}`);
        continue;
      }
      // Show changed rows first, then unchanged
      rows.push({
        key,
        name: info.name,
        emoji: info.emoji,
        unit: info.unit,
        oldValue: typeof oldValue === 'number' ? oldValue : null,
        newValue: typeof newValue === 'number' ? newValue : Number(newValue),
        changed,
      });
    }
    
    // Sort: changed rows first
    rows.sort((a, b) => (a.changed === b.changed ? 0 : a.changed ? -1 : 1));
    return rows;
  };

  // Build initial estimate rows from suggestedValues
  const buildInitialEstimateRows = () => {
    const rows: Array<{ key: string; name: string; emoji: string; unit: string; value: number }> = [];
    const excludeKeys = ['inputSummary', 'outputCalculationExplanation', 'calorie_breakdown', 'items', 'references', 'aiComment', 'healthierReason', 'suggestedCustomMetrics'];
    
    if (!suggestedValues) return rows;
    
    for (const [key, value] of Object.entries(suggestedValues)) {
      if (excludeKeys.includes(key)) continue;
      if (value === undefined || value === null || value === 0) continue;
      if (typeof value !== 'number') continue;
      
      // Validate metric key exists in our definitions
      if (!isValidMetricKey(key)) {
        console.warn(`[FixInChatDialog] Filtered out invalid metric key in estimates: ${key}`);
        continue;
      }
      
      const info = getMetricInfo(key);
      if (!info) {
        console.warn(`[FixInChatDialog] Could not get metric info for: ${key}`);
        continue;
      }
      rows.push({
        key,
        name: info.name,
        emoji: info.emoji,
        unit: info.unit,
        value: value,
      });
    }
    
    return rows;
  };

  // Build initial message from suggestedValues data (no async needed!)
  // Returns just the text content - table is rendered separately in JSX
  const buildInitialMessage = (): string => {
    const inputSummary = suggestedValues?.inputSummary;
    const outputCalculationExplanation = suggestedValues?.outputCalculationExplanation;
    
    if (inputSummary && outputCalculationExplanation) {
      return `${inputSummary}\n\n${outputCalculationExplanation}\n\nLet me know if anything looks off and I'll help you fix it!`;
    }
    
    // Fallback for legacy data without these fields
    const items = suggestedValues?.items || [];
    const itemDescriptions = items.map((item: any) => {
      if (item.quantity && item.quantity !== 1) {
        return `${item.quantity}x ${item.name}`;
      }
      return item.name;
    }).filter(Boolean);
    
    const loggedDescription = itemDescriptions.length > 0 
      ? itemDescriptions.join(", ")
      : (originalInput ? `"${originalInput}"` : "your entry");

    const calorieBreakdown = suggestedValues?.calorie_breakdown;
    let explanation = "";
    if (calorieBreakdown) {
      explanation = `Here's how I calculated it: ${calorieBreakdown}`;
    } else {
      explanation = "I've estimated the values based on the information provided.";
    }

    return `You logged ${loggedDescription}.\n\n${explanation}\n\nLet me know if anything looks off and I'll help you fix it!`;
  };

  // Check if this is the initial message (first message, from assistant)
  const isInitialMessage = (index: number) => index === 0 && messages[0]?.role === "assistant";

  // Fetch free fix status from database when dialog opens
  useEffect(() => {
    if (open && userId) {
      // Check database for free fix availability
      supabase.rpc('has_free_fix', { p_user_id: userId })
        .then(({ data, error }) => {
          if (!error && data !== null) {
            console.log('[FixInChatDialog] Free fix from DB:', data);
            setFreeFixFromDb(data);
          }
        });
    }
  }, [open, userId]);

  // Set initial message when dialog opens
  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{
        role: "assistant",
        content: buildInitialMessage(),
      }]);
    }
  }, [open]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setMessages([]);
      setInputValue("");
      setIsLoading(false);
      setHasUsedFreeFix(false);
      setFreeFixFromDb(null);
    }
  }, [open]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue("");
    if (inputRef.current) inputRef.current.style.height = 'auto';
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setIsLoading(true);

    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error(t('notifications.toast.please_sign_in'));
        setIsLoading(false);
        return;
      }

      // Get the conversation history for context
      const conversationHistory = messages.map(m => ({
        role: m.role,
        content: m.content,
      }));

      const response = await supabase.functions.invoke("fix-in-chat", {
        body: {
          action: "chat",
          userMessage,
          conversationHistory,
          originalInput,
          suggestedValues,
          customMetrics,
        },
      });

      if (response.error) {
        if (response.error.message?.includes("insufficient_credits")) {
          toast.error(t('notifications.toast.insufficient_credits'));
          setIsLoading(false);
          return;
        }
        throw new Error(response.error.message || "Failed to get response");
      }

      const { message, updatedValues, creditsRemaining, usedFreeFix } = response.data;

      // Track if free fix was used
      if (usedFreeFix) {
        setHasUsedFreeFix(true);
        onFreeFixUsed?.();
      }

      // Update credits display
      queryClient.invalidateQueries({ queryKey: ["user-credits", userId] });

      setMessages(prev => [...prev, {
        role: "assistant",
        content: message,
        updatedValues: updatedValues || undefined,
      }]);
    } catch (error: any) {
      console.error("[FixInChatDialog] Error sending message:", error);
      toast.error(t('notifications.toast.failed_to_send_message'));
      // Remove the user message if the request failed
      setMessages(prev => prev.slice(0, -1));
      setInputValue(userMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogValues = async (values: Record<string, any>) => {
    // Filter out invalid metric keys before logging to prevent database errors
    const filteredValues: Record<string, any> = {};
    for (const [key, value] of Object.entries(values)) {
      if (isValidMetricKey(key)) {
        filteredValues[key] = value;
      } else {
        console.warn(`[FixInChatDialog] Filtered out invalid metric key before logging: ${key}`);
      }
    }
    hapticSuccess();
    onLogValues(filteredValues);
    onOpenChange(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full h-full max-w-full max-h-full rounded-none border-0 flex flex-col" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            {t('fix_in_chat.title')}
          </DialogTitle>
        </DialogHeader>

        {/* Chat Messages Area */}
        <div className="flex-1 overflow-y-auto min-h-0 space-y-4 py-4">
          <AnimatePresence initial={false}>
            {messages.map((message, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] rounded-lg px-4 py-2.5 ${
                      message.role === "user"
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted"
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    
                    {/* Initial Estimates Table - only on first assistant message */}
                    {isInitialMessage(index) && (() => {
                      const estimateRows = buildInitialEstimateRows();
                      if (estimateRows.length === 0) return null;
                      return (
                        <div className="mt-3 pt-3 border-t border-border/50">
                          <div className="rounded-md overflow-hidden border border-border/50">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="bg-muted/50">
                                  <th className="text-left py-1.5 px-2 font-medium">{t('fix_in_chat.metric')}</th>
                                  <th className="text-right py-1.5 px-2 font-medium">{t('fix_in_chat.estimate')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {estimateRows.map((row) => (
                                  <tr key={row.key} className="border-t border-border/30">
                                    <td className="py-1.5 px-2">
                                      <span className="mr-1">{row.emoji}</span>
                                      {row.name}
                                    </td>
                                    <td className="text-right py-1.5 px-2 font-medium">
                                      {row.value}{row.unit ? ` ${row.unit}` : ''}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })()}
                    
                    {/* Log Values Button for assistant messages with updated values */}
                    {message.role === "assistant" && message.updatedValues && (() => {
                      const comparisonRows = buildComparisonRows(message.updatedValues);
                      return (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.3 }}
                          className="mt-3 pt-3 border-t border-border/50"
                        >
                          {/* Comparison Table */}
                          {comparisonRows.length > 0 && (
                            <div className="mb-3 rounded-md overflow-hidden border border-border/50">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="bg-muted/50">
                                    <th className="text-left py-1.5 px-2 font-medium">{t('fix_in_chat.metric')}</th>
                                    <th className="text-right py-1.5 px-2 font-medium text-muted-foreground">{t('fix_in_chat.old')}</th>
                                    <th className="text-right py-1.5 px-2 font-medium text-primary">{t('fix_in_chat.new')}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {comparisonRows.map((row) => (
                                    <tr key={row.key} className={`border-t border-border/30 ${!row.changed ? 'opacity-60' : ''}`}>
                                      <td className="py-1.5 px-2">
                                        <span className="mr-1">{row.emoji}</span>
                                        {row.name}
                                      </td>
                                      <td className="text-right py-1.5 px-2 text-muted-foreground">
                                        {row.oldValue !== null ? `${row.oldValue}${row.unit ? ` ${row.unit}` : ''}` : '—'}
                                      </td>
                                      <td className={`text-right py-1.5 px-2 font-medium ${row.changed ? 'text-primary' : 'text-muted-foreground'}`}>
                                        {row.newValue}{row.unit ? ` ${row.unit}` : ''}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                          <Button
                            size="sm"
                            onClick={() => handleLogValues(message.updatedValues!)}
                            className="w-full gap-2"
                          >
                            <Check className="h-4 w-4" />
                            {t('fix_in_chat.log_new_values')}
                          </Button>
                        </motion.div>
                      );
                    })()}
                  </div>
                </motion.div>
            ))}
          </AnimatePresence>
          
          {/* Loading indicator for AI response */}
          {isLoading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex justify-start"
            >
              <div className="bg-muted rounded-lg px-4 py-2.5">
                <div className="flex items-center gap-2">
                  <MiniGridLoader className="h-4 w-4" />
                  <span className="text-sm text-muted-foreground">{t('fix_in_chat.thinking')}</span>
                </div>
              </div>
            </motion.div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="flex-shrink-0 pt-2">
          <div className="flex gap-2">
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                autoResizeInput();
              }}
              onKeyDown={handleKeyDown}
              placeholder={t('fix_in_chat.input_placeholder')}
              disabled={isLoading}
              rows={1}
              className="flex-1 rounded-2xl flex w-full border border-input bg-background px-3 py-2 text-base md:text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none overflow-hidden"
            />
            <Button
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isLoading}
              size="icon"
            >
              {isLoading ? (
                <MiniGridLoader className="h-4 w-4" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
          {/* Show "Free" indicator when free fix is available and not yet used */}
          {freeFixAvailable && !hasUsedFreeFix ? (
            <p className="text-xs text-center mt-2">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full font-medium">
                ✨ {t('fix_in_chat.first_message_free')}
              </span>
            </p>
          ) : (
            <>
              <p className="text-xs text-muted-foreground mt-2 text-center">
                {t('fix_in_chat.credit_cost')}
              </p>
              <button
                onClick={() => navigate(isPremium ? "/credits-success" : "/upgrade")}
                className="text-xs text-muted-foreground/60 text-center flex items-center justify-center gap-0.5 mx-auto hover:text-muted-foreground transition-colors cursor-pointer"
              >
                {t('fix_in_chat.you_have_credits', { credits: typeof userCredits === 'number' ? (userCredits / 10).toFixed(1) : '...' })}
                <ChevronRight className="h-3 w-3" />
              </button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
